import { __awaiter } from "tslib";
/**
 * Copyright 2022-2025, Optimizely
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { sprintf } from '../utils/fns';
import { EventEmitter } from '../utils/event_emitter/event_emitter';
import { DEFAULT_AUTHENTICATED_URL_TEMPLATE, DEFAULT_URL_TEMPLATE } from './constant';
import { BaseService, ServiceState } from '../service';
import { isSuccessStatusCode } from '../utils/http_request_handler/http_util';
import { DATAFILE_FETCH_REQUEST_FAILED, ERROR_FETCHING_DATAFILE, } from 'error_message';
import { ADDING_AUTHORIZATION_HEADER_WITH_BEARER_TOKEN, MAKING_DATAFILE_REQ_TO_URL_WITH_HEADERS, RESPONSE_STATUS_CODE, SAVED_LAST_MODIFIED_HEADER_VALUE_FROM_RESPONSE, } from 'log_message';
export const LOGGER_NAME = 'PollingDatafileManager';
import { SERVICE_STOPPED_BEFORE_RUNNING } from '../service';
export const FAILED_TO_FETCH_DATAFILE = 'Failed to fetch datafile';
export class PollingDatafileManager extends BaseService {
    constructor(config) {
        super(config.startupLogs);
        const { autoUpdate = false, sdkKey, datafileAccessToken, customHeaders, urlTemplate, cache, initRetry, repeater, requestHandler, logger, } = config;
        this.cache = cache;
        this.cacheKey = 'opt-datafile-' + sdkKey;
        this.sdkKey = sdkKey;
        this.datafileAccessToken = datafileAccessToken;
        this.customHeaders = customHeaders;
        this.requestHandler = requestHandler;
        this.emitter = new EventEmitter();
        this.autoUpdate = autoUpdate;
        this.initRetryRemaining = initRetry;
        this.repeater = repeater;
        if (logger) {
            this.setLogger(logger);
        }
        const urlTemplateToUse = urlTemplate ||
            (datafileAccessToken ? DEFAULT_AUTHENTICATED_URL_TEMPLATE : DEFAULT_URL_TEMPLATE);
        this.datafileUrl = sprintf(urlTemplateToUse, this.sdkKey);
    }
    setLogger(logger) {
        this.logger = logger;
        this.logger.setName(LOGGER_NAME);
    }
    onUpdate(listener) {
        return this.emitter.on('update', listener);
    }
    get() {
        return this.currentDatafile;
    }
    start() {
        if (!this.isNew()) {
            return;
        }
        super.start();
        this.state = ServiceState.Starting;
        this.setDatafileFromCacheIfAvailable();
        this.repeater.setTask(this.syncDatafile.bind(this));
        this.repeater.start(true);
    }
    makeDisposable() {
        var _a;
        super.makeDisposable();
        this.initRetryRemaining = Math.min((_a = this.initRetryRemaining) !== null && _a !== void 0 ? _a : 5, 5);
    }
    stop() {
        var _a;
        if (this.isDone()) {
            return;
        }
        if (this.isNew() || this.isStarting()) {
            this.startPromise.reject(new Error(sprintf(SERVICE_STOPPED_BEFORE_RUNNING, 'PollingDatafileManager')));
        }
        this.state = ServiceState.Terminated;
        this.repeater.stop();
        (_a = this.currentRequest) === null || _a === void 0 ? void 0 : _a.abort();
        this.emitter.removeAllListeners();
        this.stopPromise.resolve();
    }
    handleInitFailure() {
        this.state = ServiceState.Failed;
        this.repeater.stop();
        const error = new Error(FAILED_TO_FETCH_DATAFILE);
        this.startPromise.reject(error);
        this.stopPromise.reject(error);
    }
    handleError(errorOrStatus) {
        var _a, _b;
        if (this.isDone()) {
            return;
        }
        if (errorOrStatus instanceof Error) {
            (_a = this.logger) === null || _a === void 0 ? void 0 : _a.error(ERROR_FETCHING_DATAFILE, errorOrStatus.message, errorOrStatus);
        }
        else {
            (_b = this.logger) === null || _b === void 0 ? void 0 : _b.error(DATAFILE_FETCH_REQUEST_FAILED, errorOrStatus);
        }
        if (this.isStarting() && this.initRetryRemaining !== undefined) {
            if (this.initRetryRemaining === 0) {
                this.handleInitFailure();
            }
            else {
                this.initRetryRemaining--;
            }
        }
    }
    onRequestRejected(err) {
        return __awaiter(this, void 0, void 0, function* () {
            this.handleError(err);
            return Promise.reject(err);
        });
    }
    onRequestResolved(response) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.isDone()) {
                return;
            }
            this.saveLastModified(response.headers);
            if (!isSuccessStatusCode(response.statusCode)) {
                this.handleError(response.statusCode);
                return Promise.reject(new Error());
            }
            const datafile = this.getDatafileFromResponse(response);
            if (datafile) {
                this.handleDatafile(datafile);
                // if autoUpdate is off, don't need to sync datafile any more
                // if disposable, stop the repeater after the first successful fetch
                if (!this.autoUpdate || this.disposable) {
                    this.repeater.stop();
                }
            }
        });
    }
    makeDatafileRequest() {
        var _a, _b;
        const headers = Object.assign({}, this.customHeaders);
        if (this.lastResponseLastModified) {
            headers['if-modified-since'] = this.lastResponseLastModified;
        }
        if (this.datafileAccessToken) {
            (_a = this.logger) === null || _a === void 0 ? void 0 : _a.debug(ADDING_AUTHORIZATION_HEADER_WITH_BEARER_TOKEN);
            headers['Authorization'] = `Bearer ${this.datafileAccessToken}`;
        }
        (_b = this.logger) === null || _b === void 0 ? void 0 : _b.debug(MAKING_DATAFILE_REQ_TO_URL_WITH_HEADERS, this.datafileUrl, () => JSON.stringify(headers));
        return this.requestHandler.makeRequest(this.datafileUrl, headers, 'GET');
    }
    syncDatafile() {
        return __awaiter(this, void 0, void 0, function* () {
            this.currentRequest = this.makeDatafileRequest();
            return this.currentRequest.responsePromise
                .then(this.onRequestResolved.bind(this), this.onRequestRejected.bind(this))
                .finally(() => this.currentRequest = undefined);
        });
    }
    handleDatafile(datafile) {
        var _a;
        if (this.isDone()) {
            return;
        }
        this.currentDatafile = datafile;
        (_a = this.cache) === null || _a === void 0 ? void 0 : _a.set(this.cacheKey, datafile);
        if (this.isStarting()) {
            this.startPromise.resolve();
            this.state = ServiceState.Running;
        }
        this.emitter.emit('update', datafile);
    }
    getDatafileFromResponse(response) {
        var _a;
        (_a = this.logger) === null || _a === void 0 ? void 0 : _a.debug(RESPONSE_STATUS_CODE, response.statusCode);
        if (response.statusCode === 304) {
            return undefined;
        }
        return response.body;
    }
    saveLastModified(headers) {
        var _a;
        const lastModifiedHeader = headers['last-modified'] || headers['Last-Modified'];
        if (lastModifiedHeader !== undefined) {
            this.lastResponseLastModified = lastModifiedHeader;
            (_a = this.logger) === null || _a === void 0 ? void 0 : _a.debug(SAVED_LAST_MODIFIED_HEADER_VALUE_FROM_RESPONSE, this.lastResponseLastModified);
        }
    }
    setDatafileFromCacheIfAvailable() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.cache) {
                return;
            }
            try {
                const datafile = yield this.cache.get(this.cacheKey);
                if (datafile && this.isStarting()) {
                    this.handleDatafile(datafile);
                }
            }
            catch (_a) {
                // ignore error
            }
        });
    }
}
export const __platforms = ['__universal__'];
//# sourceMappingURL=polling_datafile_manager.js.map